/**
 * Invoice hooks using TanStack Query
 *
 * Slice A: list query, single query, create mutation, void mutation.
 * Slice B (PR B) adds: usePaymentLinks, useCreatePaymentLink, useRevokePaymentLink.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { Invoice, InvoiceCreateInput, PaymentHistoryItem } from '@aelita/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InvoiceFilters {
  page?: number;
  limit?: number;
  status?: string;
  clientId?: string;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface InvoiceListResponse {
  data: Invoice[];
  pagination: PaginationMeta;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useInvoices(filters: InvoiceFilters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });

  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: async () => {
      const response = await api.get<Invoice[]>(`/invoices?${params.toString()}`);
      return response as unknown as InvoiceListResponse;
    },
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const response = await api.get<Invoice>(`/invoices/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InvoiceCreateInput) => {
      const response = await api.post<Invoice>('/invoices', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<Invoice>(`/invoices/${id}/void`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    },
  });
}

export function useInvoicePayments(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ['invoice-payments', invoiceId],
    queryFn: async () => {
      const response = await api.get<PaymentHistoryItem[]>(`/invoices/${invoiceId}/payments`);
      return response.data ?? [];
    },
    enabled: !!invoiceId,
  });
}

export interface RecordPaymentInput {
  amount: number;
  method: 'other';
  paidAt: string; // ISO 8601
}

export function useRecordPayment(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordPaymentInput) => {
      const response = await api.post<PaymentHistoryItem>(`/invoices/${invoiceId}/payments`, input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
